import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Package, 
  Clock, 
  Calendar,
  XCircle,
  Loader2,
  AlertTriangle,
  TrendingDown,
  TrendingUp as TrendingUpIcon
} from 'lucide-react';
import StockMovementAnalysis from './StockMovementAnalysis';
import AverageDeliveryTime from './AverageDeliveryTime';
import PeakHoursAnalysis from './PeakHoursAnalysis';
import RejectionAnalysis from './RejectionAnalysis';

function Phase3Dashboard({ userId, period, customStartDate, customEndDate }) {
  const [stockMovement, setStockMovement] = useState(null);
  const [deliveryTime, setDeliveryTime] = useState(null);
  const [peakHours, setPeakHours] = useState(null);
  const [rejectionData, setRejectionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    fetchAllData();
  }, [userId, period, customStartDate, customEndDate]);

  async function fetchAllData() {
    setLoading(true);
    setError(null);

    try {
      const params = {
        admin_user_id: userId,
        period: period
      };

      if (period === 'custom' && customStartDate && customEndDate) {
        params.start_date = customStartDate;
        params.end_date = customEndDate;
      }

      const [stock, time, peaks, rejections] = await Promise.all([
        axios.get(`${baseUrl}/api/stats/stock-movement`, { params }),
        axios.get(`${baseUrl}/api/stats/average-delivery-time`, { params }),
        axios.get(`${baseUrl}/api/stats/peak-hours-days`, { params }),
        axios.get(`${baseUrl}/api/stats/rejection-analysis`, { params })
      ]);

      setStockMovement(stock.data);
      setDeliveryTime(time.data);
      setPeakHours(peaks.data);
      setRejectionData(rejections.data);
    } catch (err) {
      console.error('Error fetching Phase 3 statistics:', err);
      setError('Error al cargar las estadísticas de operaciones');
    } finally {
      setLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-12 h-12 text-[#B1336E] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3" />
        <p className="text-red-700 font-montserrat">{error}</p>
        <button
          onClick={fetchAllData}
          className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg font-montserrat hover:bg-red-700 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stock Movement */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <Package className="w-6 h-6 text-[#B1336E]" />
          Movimiento de Inventario
        </h2>
        <StockMovementAnalysis data={stockMovement} />
      </div>

      {/* Average Delivery Time */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <Clock className="w-6 h-6 text-[#B1336E]" />
          Tiempo Promedio de Entrega
        </h2>
        <AverageDeliveryTime data={deliveryTime} />
      </div>

      {/* Peak Hours Analysis */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <Calendar className="w-6 h-6 text-[#B1336E]" />
          Análisis de Horas y Días Pico
        </h2>
        <PeakHoursAnalysis data={peakHours} />
      </div>

      {/* Rejection Analysis */}
      <div>
        <h2 className="text-xl font-bold text-slate-900 font-montserrat mb-4 flex items-center gap-2">
          <XCircle className="w-6 h-6 text-[#B1336E]" />
          Análisis de Rechazos
        </h2>
        <RejectionAnalysis data={rejectionData} />
      </div>
    </div>
  );
}

export default Phase3Dashboard;